<template>
  <BlockFieldset class="project-posts">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <b-card v-else :title="$t('posts') + ' (' + posts.length + ')'" class="mb-4">
      <div class="posts-container" ref="postsContainer">
        <Posts
          :posts="posts"
          :deleteable="!isBlockReadOnly"
          :user="getCurrentUser"
          v-on:comment:post="onCreatePost"
          v-on:comment:delete="onDeletePost"
        />
      </div>
    </b-card>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import Posts from '@/components/Posts';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** Same interval as ProjectCardPosts. */
const POLLING_INTERVAL_MILLISECONDS = 30000;

/**
 * Block project-posts ("Nachrichten", see feature page projekt-kommunikation): list, write and
 * delete own posts. `project` is the block data (`Project.blocks.posts`). Writing and deleting
 * use the existing post actions, then reload the block; the block is polled every 30 s through
 * `fetchProjectPostsBlock`, which the loading plugin skips. ProjectCardPosts stays for
 * MobileProjects.
 */
export default {
  name: 'ProjectPosts',
  components: {
    BlockFieldset,
    Posts,
  },
  mixins: [AccessBlockMixin],
  props: {
    project: { type: Object, required: true },
  },
  data() {
    return {
      pollingInterval: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['getCurrentUser']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    posts() {
      return this.project.posts ?? [];
    },
  },
  methods: {
    ...mapActions(['addPostToProject', 'deletePostFromProject', 'fetchProjectPostsBlock']),
    reloadBlock() {
      return this.fetchProjectPostsBlock(this.project.number);
    },
    async onCreatePost(content) {
      await this.addPostToProject({
        content,
        user: this.getCurrentUser.id,
        project: this.project.id,
      });
      await this.reloadBlock();
    },
    async onDeletePost(postId) {
      await this.deletePostFromProject({ postId, projectId: this.project.id });
      await this.reloadBlock();
    },
    startPolling() {
      this.pollingInterval = setInterval(this.reloadBlock, POLLING_INTERVAL_MILLISECONDS);
    },
    stopPolling() {
      clearInterval(this.pollingInterval);
    },
    scrollToBottom() {
      if (this.$refs.postsContainer) {
        this.$refs.postsContainer.scrollTop = this.$refs.postsContainer.scrollHeight;
      }
    },
  },
  mounted() {
    if (this.isNewProject) {
      return;
    }
    this.startPolling();
    this.$nextTick(this.scrollToBottom);
  },
  beforeDestroy() {
    this.stopPolling();
  },
};
</script>

<style scoped lang="scss">
.posts-container {
  max-height: 370px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}
</style>
